import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import ImageField from "./ImageField";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

function CategoryForm({ category, onClose, onSaved }) {
  const isEdit = Boolean(category);
  const [form, setForm] = useState({
    name: category?.name || "",
    tagline: category?.tagline || "",
    image: category?.image || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Category name is required.");
    setSaving(true);
    setError("");
    try {
      if (isEdit) await api.patch(`/admin/categories/${category.id}`, form);
      else await api.post("/admin/categories", form);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((Array.isArray(detail) ? detail[0]?.msg : detail) || "Could not save the category.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-8">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div data-testid="category-form" role="dialog" aria-modal="true" className="relative w-full max-w-lg rounded-t-3xl bg-white p-6 md:rounded-3xl md:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-serif text-2xl font-medium">{isEdit ? "Edit Category" : "Add Category"}</h3>
          <button data-testid="category-form-close" onClick={onClose} aria-label="Close" className="flex h-10 w-10 items-center justify-center rounded-full border border-line transition-colors hover:bg-ink hover:text-cream">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={save} className="flex flex-col gap-5">
          <div>
            <label className={labelCls}>Name</label>
            <input data-testid="category-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="e.g. Wedding Gifts" />
            {!isEdit && <p className="mt-1.5 text-[11px] text-[#5C5C5C]">A URL-safe slug is created automatically and stays fixed so product assignments never break.</p>}
          </div>
          <div>
            <label className={labelCls}>Tagline</label>
            <input data-testid="category-tagline-input" value={form.tagline} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} className={inputCls} placeholder="Short line under the name" />
          </div>
          <ImageField label="Category Image" value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testid="category-image" />
          {error && <p data-testid="category-form-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
          <button data-testid="category-save-button" type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : isEdit ? "Save Category" : "Add Category"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const patch = async (id, payload) => {
    try {
      await api.patch(`/admin/categories/${id}`, payload);
      load();
    } catch {
      toast.error("Could not update the category.");
    }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    const a = next[index], b = next[j];
    try {
      await api.patch(`/admin/categories/${a.id}`, { sort_order: b.sort_order });
      await api.patch(`/admin/categories/${b.id}`, { sort_order: a.sort_order });
      load();
    } catch {
      toast.error("Could not reorder categories.");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the "${c.name}" category?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error((Array.isArray(detail) ? detail[0]?.msg : detail) || "Could not delete the category.");
    }
  };

  return (
    <section data-testid="admin-categories">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-[#5C5C5C]">Categories appear on the homepage, in shop filters and in product forms automatically.</p>
        <button data-testid="add-category-button" onClick={() => setEditing("new")} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white">
          <Plus size={15} /> Add Category
        </button>
      </div>

      {!items ? (
        <div className="flex justify-center py-20"><Loader2 size={26} className="animate-spin text-rose" /></div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((c, i) => (
            <div key={c.id} data-testid={`category-row-${c.id}`} className={`flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-white p-4 ${!c.visible ? "opacity-60" : ""}`}>
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`category-up-${c.id}`} className="p-1 text-[#5C5C5C] hover:text-rose disabled:opacity-30"><ArrowUp size={13} /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down" data-testid={`category-down-${c.id}`} className="p-1 text-[#5C5C5C] hover:text-rose disabled:opacity-30"><ArrowDown size={13} /></button>
              </div>
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-line bg-cream">
                {c.image && <img src={imgUrl(c.image)} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-serif text-lg font-medium">{c.name}</p>
                <p className="truncate text-xs text-[#5C5C5C]">{c.slug} · {c.tagline}</p>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                <input data-testid={`category-visible-${c.id}`} type="checkbox" checked={c.visible} onChange={(e) => patch(c.id, { visible: e.target.checked })} className="h-4 w-4 accent-[#C48B81]" />
                Visible
              </label>
              <button data-testid={`category-edit-${c.id}`} onClick={() => setEditing(c)} className="inline-flex items-center gap-1.5 rounded-full border border-ink px-4 py-2 text-xs font-medium transition-colors hover:bg-ink hover:text-cream">
                <Pencil size={12} /> Edit
              </button>
              <button data-testid={`category-delete-${c.id}`} onClick={() => remove(c)} aria-label={`Delete ${c.name}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-[#5C5C5C] transition-colors hover:border-red-400 hover:text-red-500">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <CategoryForm category={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </section>
  );
}
